import { MAPS } from '@cloudpearl/config';
import type { MapId } from '@cloudpearl/config';
import { World } from '@cloudpearl/core';
import { buildTile, buildUnderside, islandsOfTile } from '@cloudpearl/assets/worldgen';
import type { Lod, TileData, UndersideData } from '@cloudpearl/assets/worldgen';
import type { WorkerIn } from './tile.worker';

type Job = { kind: 'tile'; ti: number; tj: number; lod: Lod; key: string; prio: number } | { kind: 'islands'; ti: number; tj: number; key: string; prio: number };

export type StreamResult = { kind: 'tile'; data: TileData } | { kind: 'islands'; ti: number; tj: number; list: UndersideData[] };

/**
 * A small pool of terrain workers with a priority queue (nearest first). Falls back to time-sliced
 * generation on the main thread when workers are unavailable.
 */
export class TileStreamer {
  private workers: Worker[] = [];
  private busy: number[] = [];
  private generation = 0;
  private readonly queue: Job[] = [];
  private readonly queued = new Set<string>();
  private readonly inFlight = new Set<string>();
  private readonly results: StreamResult[] = [];
  private syncWorld: World | null = null;
  private seed: number;
  private mapId: MapId;

  constructor(seed: number, mapId: MapId) {
    this.seed = seed;
    this.mapId = mapId;
    const n = Math.max(1, Math.min(3, (navigator.hardwareConcurrency ?? 4) - 2));
    try {
      for (let i = 0; i < n; i++) {
        const w = new Worker(new URL('./tile.worker.ts', import.meta.url), { type: 'module' });
        const idx = i;
        w.onmessage = (e: MessageEvent<{ type: 'tile'; generation: number; data: TileData } | { type: 'islands'; generation: number; ti: number; tj: number; list: UndersideData[] }>) => {
          const m = e.data;
          this.busy[idx] = Math.max(0, this.busy[idx]! - 1);
          if (m.generation !== this.generation) return;
          if (m.type === 'tile') {
            this.inFlight.delete(`t${m.data.ti},${m.data.tj},${m.data.lod}`);
            this.results.push({ kind: 'tile', data: m.data });
          } else {
            this.inFlight.delete(`i${m.ti},${m.tj}`);
            this.results.push({ kind: 'islands', ti: m.ti, tj: m.tj, list: m.list });
          }
          // Keep workers fed even when frames are slow (or the tab is throttled).
          this.dispatch();
        };
        w.onerror = (err) => {
          console.warn('[cloudpearl] terrain worker failed, falling back to main thread', err);
          for (const x of this.workers) x.terminate();
          this.workers = [];
        };
        this.workers.push(w);
        this.busy.push(0);
      }
    } catch {
      this.workers = [];
    }
    this.reset(seed, mapId);
  }

  reset(seed: number, mapId: MapId): void {
    this.seed = seed;
    this.mapId = mapId;
    this.generation++;
    this.queue.length = 0;
    this.queued.clear();
    this.inFlight.clear();
    this.results.length = 0;
    this.syncWorld = null;
    this.busy = this.busy.map(() => 0);
    const msg: WorkerIn = { type: 'init', seed, mapId, generation: this.generation };
    for (const w of this.workers) w.postMessage(msg);
  }

  /** Clear the wish list; call before re-requesting each streaming update. */
  beginRequests(): void {
    this.queue.length = 0;
    this.queued.clear();
  }

  requestTile(ti: number, tj: number, lod: Lod, prio: number): void {
    const key = `t${ti},${tj},${lod}`;
    if (this.queued.has(key) || this.inFlight.has(key)) return;
    this.queued.add(key);
    this.queue.push({ kind: 'tile', ti, tj, lod, key, prio });
    this.sorted = false;
  }

  requestIslands(ti: number, tj: number, prio: number): void {
    const key = `i${ti},${tj}`;
    if (this.queued.has(key) || this.inFlight.has(key)) return;
    this.queued.add(key);
    this.queue.push({ kind: 'islands', ti, tj, key, prio });
    this.sorted = false;
  }

  get pending(): number {
    return this.queue.length + this.inFlight.size;
  }

  private sorted = false;

  private dispatch(): void {
    if (!this.sorted) {
      this.queue.sort((a, b) => a.prio - b.prio);
      this.sorted = true;
    }
    {
      const perWorker = 3;
      let wi = 0;
      while (this.queue.length) {
        // Least-busy worker.
        let best = -1;
        for (let i = 0; i < this.workers.length; i++) {
          const k = (wi + i) % this.workers.length;
          if (this.busy[k]! < perWorker && (best < 0 || this.busy[k]! < this.busy[best]!)) best = k;
        }
        if (best < 0) break;
        const job = this.queue.shift()!;
        this.queued.delete(job.key);
        this.inFlight.add(job.key);
        this.busy[best]!++;
        wi = best + 1;
        const msg: WorkerIn =
          job.kind === 'tile'
            ? { type: 'tile', ti: job.ti, tj: job.tj, lod: job.lod, generation: this.generation }
            : { type: 'islands', ti: job.ti, tj: job.tj, generation: this.generation };
        this.workers[best]!.postMessage(msg);
      }
    }
  }

  /** Dispatch work; returns finished results. */
  pump(): StreamResult[] {
    if (this.workers.length) {
      this.dispatch();
    } else {
      this.queue.sort((a, b) => a.prio - b.prio);
      // Main-thread fallback: ~6 ms of work per frame.
      if (!this.syncWorld) this.syncWorld = new World(this.seed, MAPS[this.mapId]);
      const w = this.syncWorld;
      const t0 = performance.now();
      while (this.queue.length && performance.now() - t0 < 6) {
        const job = this.queue.shift()!;
        this.queued.delete(job.key);
        if (job.kind === 'tile') this.results.push({ kind: 'tile', data: buildTile(w, job.ti, job.tj, job.lod) });
        else {
          const p = w.map.palette;
          this.results.push({ kind: 'islands', ti: job.ti, tj: job.tj, list: islandsOfTile(w, job.ti, job.tj).map((isl) => buildUnderside(w, isl, { cliff: p.cliff, cliffDark: p.cliffDark })) });
        }
      }
    }
    const out = this.results.splice(0, this.results.length);
    return out;
  }

  dispose(): void {
    for (const w of this.workers) w.terminate();
    this.workers = [];
  }
}
